import React from 'react';
import { MapPin, ShieldCheck, Clock, EyeOff } from 'lucide-react';

/**
 * What the app tells employees about location capture.
 *
 * Kept in one place so the priming dialog and any help/settings screen
 * cannot drift apart. RETENTION_DAYS must match the backend cleanup job —
 * if the agreed policy changes, change it here and there together.
 */
export const RETENTION_DAYS = 90;

export const LOCATION_PRIVACY_POINTS = [
  {
    icon: MapPin,
    title: 'What is captured',
    body: 'Your device position at the moment you check in and check out — nothing in between. The app never tracks you during the day or in the background.',
  },
  {
    icon: ShieldCheck,
    title: 'Why',
    body: 'To record whether attendance was from an office location or elsewhere, so office and remote days are logged accurately without anyone having to ask.',
  },
  {
    icon: EyeOff,
    title: 'Who can see it',
    body: 'You, and HR/admin staff who manage attendance. It is not shared with anyone else and is not used for anything besides attendance.',
  },
  {
    icon: Clock,
    title: 'How long it is kept',
    body: `The in-office / out-of-office result stays with your attendance record. The exact position is deleted after ${RETENTION_DAYS} days.`,
  },
];

export const LOCATION_OPTIONAL_NOTE =
  'Sharing your location is optional. If you decline, you can still check in — the record is simply marked "Location Unavailable".';

/**
 * One-time dialog shown before the first location request, so the browser
 * permission prompt is not the first thing an employee sees.
 *
 * Purely informational: "Continue" proceeds to the browser prompt (where the
 * real choice is made) and "Not now" checks in without location.
 */
export default function LocationPrivacyNotice({ open, onAccept, onDecline }) {
  if (!open) return null;

  return (
    <div className="lpn-overlay" role="dialog" aria-modal="true" aria-labelledby="lpn-title">
      <div className="lpn-box">
        <div className="lpn-header">
          <div className="lpn-icon"><MapPin size={20} /></div>
          <h3 id="lpn-title">Before we use your location</h3>
        </div>

        <ul className="lpn-points">
          {LOCATION_PRIVACY_POINTS.map(({ icon: Icon, title, body }) => (
            <li key={title}>
              <Icon size={15} className="lpn-point-icon" />
              <div>
                <strong>{title}</strong>
                <p>{body}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="lpn-optional">{LOCATION_OPTIONAL_NOTE}</p>

        <div className="lpn-actions">
          <button className="lpn-btn lpn-btn-secondary" onClick={onDecline}>
            Not now
          </button>
          <button className="lpn-btn lpn-btn-primary" onClick={onAccept}>
            Continue
          </button>
        </div>
      </div>

      <style>{`
        .lpn-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100000;
          padding: 16px;
        }
        .lpn-box {
          background: #fff;
          border-radius: 14px;
          padding: 24px;
          max-width: 480px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
        }
        .lpn-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 18px;
        }
        .lpn-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #e0f2fe;
          color: #0284c7;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .lpn-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
        }
        .lpn-points {
          list-style: none;
          margin: 0 0 16px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .lpn-points li {
          display: flex;
          gap: 10px;
          align-items: flex-start;
        }
        .lpn-point-icon {
          color: #0284c7;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .lpn-points strong {
          display: block;
          font-size: 13px;
          color: #0f172a;
          margin-bottom: 2px;
        }
        .lpn-points p {
          margin: 0;
          font-size: 13px;
          color: #475569;
          line-height: 1.5;
        }
        .lpn-optional {
          margin: 0 0 20px;
          padding: 10px 12px;
          background: #f1f5f9;
          border-radius: 8px;
          font-size: 12.5px;
          color: #475569;
          line-height: 1.5;
        }
        .lpn-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
        .lpn-btn {
          padding: 9px 18px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
        }
        .lpn-btn-secondary {
          background: #fff;
          border-color: #cbd5e1;
          color: #475569;
        }
        .lpn-btn-secondary:hover { background: #f8fafc; }
        .lpn-btn-primary {
          background: #0ea5e9;
          color: #fff;
        }
        .lpn-btn-primary:hover { background: #0284c7; }
      `}</style>
    </div>
  );
}
